const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const DATETIME_LOCAL_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?$/

function isDateOnlyString (value) {
  return typeof value === 'string' && DATE_ONLY_PATTERN.test(value)
}

function isDateTimeLocalString (value) {
  return typeof value === 'string' && DATETIME_LOCAL_PATTERN.test(value)
}

function getSystemTimezone () {
  if (typeof Intl === 'undefined' || !Intl.DateTimeFormat) {
    return 'UTC'
  }

  const options = Intl.DateTimeFormat().resolvedOptions()
  return options && options.timeZone ? options.timeZone : 'UTC'
}

function getDateParts (date, timezone) {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  })

  return formatter.formatToParts(date).reduce((result, part) => {
    if (part.type !== 'literal') {
      result[part.type] = Number(part.value)
    }
    return result
  }, {})
}

function padDatePart (value) {
  return value < 10 ? `0${value}` : `${value}`
}

function hourForInput (hour) {
  return hour === 24 ? 0 : hour
}

function formatDateInTimezone (date, timezone) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null
  }

  const parts = getDateParts(date, timezone)
  return `${parts.year}-${padDatePart(parts.month)}-${padDatePart(parts.day)}`
}

function formatDateTimeInTimezone (date, timezone) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null
  }

  const parts = getDateParts(date, timezone)
  return `${parts.year}-${padDatePart(parts.month)}-${padDatePart(parts.day)}T${padDatePart(hourForInput(parts.hour))}:${padDatePart(parts.minute)}`
}

function getTimezoneOffset (date, timezone) {
  const parts = getDateParts(date, timezone)
  const hour = hourForInput(parts.hour)
  const dateAsUtc = Date.UTC(parts.year, parts.month - 1, parts.day, hour, parts.minute, parts.second)
  return dateAsUtc - date.getTime()
}

function wallClockToDate (wallClock, timezone) {
  const utcGuess = Date.UTC(
    wallClock.year,
    wallClock.month - 1,
    wallClock.day,
    hourForInput(wallClock.hour || 0),
    wallClock.minute || 0,
    wallClock.second || 0
  )
  let offset = getTimezoneOffset(new Date(utcGuess), timezone)
  let date = new Date(utcGuess - offset)
  const adjustedOffset = getTimezoneOffset(date, timezone)

  if (adjustedOffset !== offset) {
    date = new Date(utcGuess - adjustedOffset)
  }

  return date
}

function parseDateTimeLocalParts (value) {
  if (typeof value !== 'string') {
    return null
  }

  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/)
  if (!match) {
    return null
  }

  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
    second: match[6] ? Number(match[6]) : 0
  }
}

function dateInputToDate (value, timezone) {
  if (!isDateOnlyString(value)) {
    return null
  }

  const parts = value.split('-').map(Number)
  return wallClockToDate({
    year: parts[0],
    month: parts[1],
    day: parts[2],
    hour: 0,
    minute: 0,
    second: 0
  }, timezone)
}

function dateTimeInputToDate (value, timezone) {
  const parts = parseDateTimeLocalParts(value)
  if (parts) {
    return wallClockToDate(parts, timezone)
  }

  return isDateOnlyString(value) ? dateInputToDate(value, timezone) : null
}

function coerceToDate (value) {
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

function normalizeDateInput (value, timezone) {
  if (!value) {
    return null
  }

  if (isDateOnlyString(value)) {
    return value
  }

  return formatDateInTimezone(coerceToDate(value), timezone)
}

function normalizeDateTimeInput (value, timezone) {
  if (!value) {
    return null
  }

  if (isDateTimeLocalString(value)) {
    return value.slice(0, 16)
  }

  if (isDateOnlyString(value)) {
    return `${value}T00:00`
  }

  return formatDateTimeInTimezone(coerceToDate(value), timezone)
}

function toFieldOutputValue (value, options) {
  const settings = options || {}
  if (!value) {
    return null
  }

  if (!settings.returnDate) {
    return value
  }

  if (settings.withTime) {
    return dateTimeInputToDate(value, settings.timezone)
  }

  return dateInputToDate(value, settings.timezone)
}

module.exports = {
  isDateOnlyString,
  isDateTimeLocalString,
  getSystemTimezone,
  hourForInput,
  formatDateInTimezone,
  formatDateTimeInTimezone,
  dateInputToDate,
  dateTimeInputToDate,
  normalizeDateInput,
  normalizeDateTimeInput,
  toFieldOutputValue
}
